
import { useEffect, useRef, useState } from 'react';

export default function Process() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Strategy & System Mapping',
      description: 'We design the infrastructure around your revenue model.',
      image:
        'https://public.readdy.ai/ai/img_res/0c0cd28eb14f0e68aa2e5cb39ccd0a31.jpg',
    },
    {
      number: '02',
      title: 'Custom Build & Deployment',
      description: 'Your AI ecosystem is built, connected, and optimized.',
      image:
        'https://readdy.ai/api/search-image?query=cinematic%20close%20up%20of%20hands%20working%20on%20sleek%20dark%20laptop%20screen%20showing%20code%20and%20automation%20workflow%20interface%20with%20silver%20and%20white%20UI%20elements%20on%20black%20background%20premium%20technology%20development%20scene%20with%20ambient%20lighting%20and%20clean%20minimal%20aesthetic&width=600&height=400&seq=proc-apple-002&orientation=landscape',
    },
    {
      number: '03',
      title: 'Integration & Training',
      description: 'Your team is trained to operate with confidence.',
      image:
        'https://readdy.ai/api/search-image?query=cinematic%20shot%20of%20a%20modern%20dark%20conference%20room%20with%20large%20screen%20displaying%20system%20integration%20dashboard%20in%20silver%20and%20white%20on%20black%20background%20professional%20team%20training%20environment%20with%20premium%20furniture%20and%20ambient%20lighting%20sophisticated%20corporate%20atmosphere&width=600&height=400&seq=proc-apple-003&orientation=landscape',
    },
    {
      number: '04',
      title: 'Optimization & Scale',
      description: 'Continuous refinement for performance growth.',
      image:
        'https://readdy.ai/api/search-image?query=dramatic%20cinematic%20visualization%20of%20ascending%20performance%20metrics%20and%20growth%20charts%20rendered%20as%20silver%20holographic%20projections%20above%20a%20dark%20polished%20surface%20premium%20data%20analytics%20and%20optimization%20concept%20with%20volumetric%20light%20rays%20and%20clean%20geometric%20design&width=600&height=400&seq=proc-apple-004&orientation=landscape',
    },
  ];

  return (
    <section ref={sectionRef} id="process" className="bg-[#111318] py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-xs font-semibold tracking-[0.35em] text-[#8C9198] mb-5 uppercase">
            How It Works
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Our System Architecture
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`group relative bg-[#1A1C21] rounded-3xl overflow-hidden cursor-pointer transition-all duration-700 hover:shadow-[0_0_60px_rgba(140,145,152,0.06)] ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index + 1) * 150}ms` }}
            >
              <div className="w-full h-56 overflow-hidden">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              <div className="p-8">
                <span className="text-xs font-bold tracking-[0.2em] text-[#8C9198]/60 uppercase">
                  Step {step.number}
                </span>
                <h3 className="text-xl font-bold text-white mt-2 mb-3 tracking-tight">
                  {step.title}
                </h3>
                <p className="text-sm text-[#8C9198] font-light leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
